/* ─── Container ─── */

.root {
  display: inline-flex;
  align-items: stretch;
  position: relative;
  border-radius: var(--shape-full);
  overflow: visible;
}

/* ─── Shared button base ─── */

.action,
.toggle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  font-family: var(--font-sans);
  font-weight: 600;
  cursor: pointer;
  position: relative;
  -webkit-tap-highlight-color: transparent;
  transition: opacity var(--motion-duration-short3)
    var(--motion-easing-standard);
}

/* ─── State layer ─── */

.action::before,
.toggle::before {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: inherit;
  background: currentColor;
  opacity: 0;
  transition: opacity var(--motion-duration-short3);
  pointer-events: none;
}

@media (hover: hover) {
  .action:hover::before,
  .toggle:hover::before {
    opacity: var(--state-hover);
  }
}

.action:focus-visible::before,
.toggle:focus-visible::before {
  opacity: var(--state-focus);
}

.action:active::before,
.toggle:active::before {
  opacity: var(--state-pressed);
}

/* ─── Focus ring ─── */

.action:focus-visible,
.toggle:focus-visible {
  z-index: 1;
}

/* ─── Disabled ─── */

.action:disabled,
.toggle:disabled {
  cursor: not-allowed;
  opacity: 0.38;
}

.action:disabled::before,
.toggle:disabled::before {
  display: none;
}

/* ─── Action button ─── */

.action {
  gap: var(--space-2);
  white-space: nowrap;
  border-start-start-radius: var(--shape-full);
  border-end-start-radius: var(--shape-full);
}

/* ─── Toggle button ─── */

.toggle {
  border-start-end-radius: var(--shape-full);
  border-end-end-radius: var(--shape-full);
}

/* ─── Divider between buttons ─── */

.divider {
  width: 1px;
  align-self: stretch;
  flex-shrink: 0;
}

/* ─── Sizes ─── */

.sm .action {
  font: var(--type-label-lg);
  letter-spacing: var(--tracking-label-lg);
  height: 2rem;
  padding-inline: var(--space-3);
}

.sm .toggle {
  height: 2rem;
  width: 2rem;
}

.md .action {
  font: var(--type-label-lg);
  letter-spacing: var(--tracking-label-lg);
  height: 2.5rem;
  padding-inline: var(--space-6);
}

.md .toggle {
  height: 2.5rem;
  width: 2.5rem;
}

.lg .action {
  font: var(--type-label-lg);
  letter-spacing: var(--tracking-label-lg);
  height: 3rem;
  padding-inline: var(--space-8);
}

.lg .toggle {
  height: 3rem;
  width: 2.75rem;
}

/* ─── Variant: filled ─── */

.filled .action,
.filled .toggle {
  --focus-ring-color: var(--md-on-primary);
  background: var(--md-primary);
  color: var(--md-on-primary);
}

.filled .divider {
  background: var(--md-on-primary);
  opacity: 0.2;
}

/* ─── Variant: tonal ─── */

.tonal .action,
.tonal .toggle {
  background: var(--md-primary-container);
  color: var(--md-on-primary-container);
}

.tonal .divider {
  background: var(--md-on-primary-container);
  opacity: 0.2;
}

/* ─── Variant: outlined ─── */

.outlined .action,
.outlined .toggle {
  background: transparent;
  color: var(--md-primary);
}

.outlined .action {
  border: 1px solid var(--md-outline);
  border-inline-end: none;
  border-start-end-radius: 0;
  border-end-end-radius: 0;
}

.outlined .toggle {
  border: 1px solid var(--md-outline);
  border-inline-start: none;
  border-start-start-radius: 0;
  border-end-start-radius: 0;
}

.outlined .divider {
  background: var(--md-outline);
}

/* ─── Variant: elevated ─── */

.elevated .action,
.elevated .toggle {
  background: var(--md-surface);
  color: var(--md-primary);
  box-shadow: var(--elevation-1);
}

.elevated .divider {
  background: var(--md-outline-variant);
}

/* ─── Icon slot ─── */

.icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

/* ─── Chevron ─── */

.chevron {
  width: 0.625rem;
  height: 0.375rem;
  transition: transform var(--motion-duration-short3)
    var(--motion-easing-standard);
}

.toggle[aria-expanded="true"] .chevron {
  transform: rotate(180deg);
}

/* ─── Color: success ─── */

.success .action,
.success .toggle {
  background: var(--status-success);
  color: var(--md-on-primary);
}

.success .divider {
  background: var(--md-on-primary);
  opacity: 0.2;
}
